import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { AuthShell as Shell } from "@/components/auth/AuthShell";
import { Alert, Divider, EMAIL_RE, Field, GoogleButton, PasswordField, StrengthMeter, SubmitButton, inputCls, FieldError, pwMeetsMin } from "@/components/auth/AuthFields";

const linkCls = "font-semibold text-cyan-700 hover:text-cyan-800 hover:underline underline-offset-4";

function useCooldown(initial) {
  const [left, setLeft] = useState(initial);
  useEffect(() => { if (left <= 0) { return undefined; } const t = setTimeout(() => setLeft(left - 1), 1000); return () => clearTimeout(t); }, [left]);
  return [left, setLeft];
}

function CodeStep({ mfa, onBack, onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [left, setLeft] = useCooldown(mfa.cooldown_seconds);
  const verify = async (e) => {
    e.preventDefault();
    if (busy) { return; }
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/login/verify", { challenge_id: mfa.challenge_id, code }); onDone(data); } catch (e2) { setErr(errMsg(e2)); setCode(""); }
    setBusy(false);
  };
  const resend = async () => {
    setErr(""); setInfo("");
    try { const { data } = await api.post("/auth/login/resend", { challenge_id: mfa.challenge_id }); setLeft(data.cooldown_seconds); setInfo("A new code has been sent."); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <Shell title="Check your email" subtitle="Enter the 6-digit code we sent to finish signing in.">
      <form onSubmit={verify} className="space-y-4" data-testid="login-code-form" noValidate>
        <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-600"><MailCheck className="h-5 w-5 shrink-0 text-cyan-700" />
          <span>Code sent to <b data-no-translate data-testid="login-code-email-hint">{mfa.email_hint}</b>. It expires in {mfa.expires_in_minutes} minutes.</span></div>
        <div><label htmlFor="login-code" className="mb-1.5 block text-[13px] font-medium text-slate-700">Verification code</label>
          <input id="login-code" data-testid="login-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus required
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className={`${inputCls(err)} h-14 text-center font-mono text-2xl tracking-[0.5em]`} /></div>
        {err && <Alert testid="login-code-error">{err}</Alert>}
        {info && <Alert tone="success" testid="login-code-info">{info}</Alert>}
        <SubmitButton busy={busy} disabled={code.length !== 6} busyText="Verifying…" testid="login-code-submit-button">Verify and sign in</SubmitButton>
        <div className="flex justify-between text-sm">
          <button type="button" onClick={onBack} className="text-slate-500 hover:text-slate-800" data-testid="login-code-back">Back to sign in</button>
          <button type="button" onClick={resend} disabled={left > 0} className={`${linkCls} disabled:font-normal disabled:text-slate-400 disabled:no-underline`} data-testid="login-code-resend">{left > 0 ? `Resend code in ${left}s` : "Resend code"}</button>
        </div>
      </form>
    </Shell>
  );
}

const welcome = (setUser) => (data) => { toast.success(`Welcome back, ${data.name?.split(" ")[0] || ""}`.trim()); setUser(data); };

export function Login() {
  const { setUser } = useAuth();
  const loc = useLocation();
  const [f, setF] = useState({ email: localStorage.getItem("dpdp.lastEmail") || "", password: "", remember: !!localStorage.getItem("dpdp.lastEmail") });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [touched, setTouched] = useState(false);
  const [mfa, setMfa] = useState(loc.state?.mfa || null);
  const errs = { email: !f.email ? "Enter your work email" : !EMAIL_RE.test(f.email) ? "Enter a valid email address" : "", password: !f.password ? "Enter your password" : "" };
  const done = welcome(setUser);
  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (busy || errs.email || errs.password) { return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", f);
      if (f.remember) { localStorage.setItem("dpdp.lastEmail", f.email); } else { localStorage.removeItem("dpdp.lastEmail"); }
      if (data.mfa_required) { setMfa(data); } else { done(data); }
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  if (mfa) { return <CodeStep mfa={mfa} onBack={() => { setMfa(null); setF({ ...f, password: "" }); }} onDone={done} />; }
  const show = (k) => (touched ? errs[k] : "");
  return (
    <Shell title="Welcome Back" subtitle="Sign in to manage your organization's privacy compliance.">
      <GoogleButton remember={f.remember} />
      <Divider text="or sign in with email" />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form" noValidate>
        <Field id="login-email" label="Work Email Address" type="email" autoComplete="email" placeholder="name@company.com" value={f.email} error={show("email")} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <PasswordField id="login-password" label="Password" autoComplete="current-password" placeholder="Enter your password" value={f.password} error={show("password")} onChange={(e) => setF({ ...f, password: e.target.value })}
          right={<Link to="/forgot-password" className="text-xs font-medium text-cyan-700 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>} />
        <label htmlFor="login-remember" className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-600">
          <Checkbox id="login-remember" checked={f.remember} onCheckedChange={(v) => setF({ ...f, remember: !!v })} data-testid="login-remember-checkbox" />Keep me signed in for 30 days
        </label>
        {err && <Alert testid="login-error">{err}</Alert>}
        <SubmitButton busy={busy} busyText="Signing in…" testid="login-submit-button">Sign In</SubmitButton>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">Don't have an account? <Link to="/register" className={linkCls} data-testid="register-link">Create an account</Link></p>
    </Shell>
  );
}

function validateSignup(f, google) {
  const e = {};
  if (f.name.trim().length < 2) { e.name = "Enter your full name"; }
  if (!google && !EMAIL_RE.test(f.email)) { e.email = "Enter a valid business email"; }
  if (f.organisation_name.trim().length < 2) { e.organisation_name = "Enter your organisation name"; }
  if (!/^[6-9]\d{9}$/.test(f.mobile)) { e.mobile = "Enter a valid 10-digit Indian mobile number"; }
  if (!google && !pwMeetsMin(f.password)) { e.password = "Use at least 8 characters with letters and numbers"; }
  if (!google && f.confirm !== f.password) { e.confirm = "Passwords don't match"; }
  if (!f.accept_terms) { e.accept_terms = "Please accept the Terms and Privacy Policy"; }
  return e;
}

function MobileField({ value, error, onChange }) {
  return (
    <div>
      <label htmlFor="register-mobile" className="mb-1.5 block text-[13px] font-medium text-slate-700">Mobile Number</label>
      <div className="flex">
        <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-600" data-testid="register-mobile-prefix">+91</span>
        <input id="register-mobile" data-testid="register-mobile-input" type="tel" inputMode="numeric" autoComplete="tel-national" placeholder="98765 43210" value={value} aria-invalid={!!error}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))} className={`${inputCls(error)} rounded-l-none`} />
      </div>
      <FieldError id="register-mobile" msg={error} />
    </div>
  );
}

export function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const { state } = useLocation();
  const google = state?.google;
  const [f, setF] = useState({ name: google?.name || "", email: google?.email || "", organisation_name: "", mobile: "", password: "", confirm: "", accept_terms: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [touched, setTouched] = useState(false);
  const errs = validateSignup(f, google);
  const show = (k) => (touched ? errs[k] : "");
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (busy || Object.keys(errs).length) { return; }
    setBusy(true); setErr("");
    const { confirm, ...rest } = f;
    const body = google ? { name: f.name, organisation_name: f.organisation_name, mobile: f.mobile, accept_terms: true, google_token: google.signup_token, remember: !!state?.remember } : rest;
    try { const { data } = await api.post("/auth/register", body); toast.success("Account created. Let's set up your organisation."); setUser(data); nav("/onboarding"); } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  return (
    <Shell wide title="Start Your Compliance Journey" subtitle="Create your account and simplify DPDP Act compliance for your organization.">
      {google ? <Alert tone="info" testid="register-google-banner">Signed in with Google as <b data-no-translate>{google.email}</b>. Add a few details to create your organisation.</Alert>
        : <><GoogleButton label="Sign up with Google" /><Divider text="or sign up with email" /></>}
      <form onSubmit={submit} className={`space-y-4 ${google ? "mt-5" : ""}`} data-testid="register-form" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="register-name" label="Full Name" autoComplete="name" placeholder="Priya Sharma" value={f.name} error={show("name")} onChange={s("name")} />
          <MobileField value={f.mobile} error={show("mobile")} onChange={(v) => setF({ ...f, mobile: v })} />
        </div>
        {!google && <Field id="register-email" label="Business Email" type="email" autoComplete="email" placeholder="priya@company.in" value={f.email} error={show("email")} onChange={s("email")} />}
        <Field id="register-org" label="Organization / Company Name" autoComplete="organization" placeholder="Your company Pvt. Ltd." value={f.organisation_name} error={show("organisation_name")} onChange={s("organisation_name")} hint="You'll be the Organisation Admin and can invite your team later." />
        {!google && <>
          <div><PasswordField id="register-password" label="Password" autoComplete="new-password" placeholder="Create a strong password" value={f.password} error={show("password")} onChange={s("password")} /><StrengthMeter value={f.password} /></div>
          <PasswordField id="register-password-confirm" label="Confirm Password" autoComplete="new-password" placeholder="Re-enter password" value={f.confirm} error={show("confirm") || (f.confirm && f.confirm !== f.password ? "Passwords don't match" : "")} onChange={s("confirm")} />
        </>}
        <div>
          <label htmlFor="register-terms" className="flex cursor-pointer items-start gap-2.5 text-[13px] leading-relaxed text-slate-600">
            <Checkbox id="register-terms" className="mt-0.5" checked={f.accept_terms} onCheckedChange={(v) => setF({ ...f, accept_terms: !!v })} data-testid="register-terms-checkbox" />
            <span>I agree to the <Link to="/legal/terms" target="_blank" className={linkCls} data-testid="register-terms-link">Terms &amp; Conditions</Link> and have read the <Link to="/legal/privacy" target="_blank" className={linkCls} data-testid="register-privacy-link">Privacy Policy</Link>.</span>
          </label>
          <FieldError id="register-terms" msg={show("accept_terms")} />
        </div>
        {err && <Alert testid="register-error">{err}</Alert>}
        <SubmitButton busy={busy} busyText="Creating your account…" testid="register-submit-button">Create Free Account</SubmitButton>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">Already have an account? <Link to="/login" className={linkCls} data-testid="login-link">Sign In</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { t } = useI18n();
  const submit = async (e) => {
    e.preventDefault();
    if (busy) { return; }
    if (!EMAIL_RE.test(email)) { setErr("Enter a valid email address"); return; }
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/forgot-password", { email }); setRes(data); } catch (e2) { toast.error(t(errMsg(e2))); }
    setBusy(false);
  };
  return (
    <Shell title="Reset your password" subtitle="Enter your work email and we'll send you a secure reset link.">
      <form onSubmit={submit} className="space-y-4" noValidate data-testid="forgot-form">
        <Field id="forgot-email" label="Work Email Address" type="email" autoComplete="email" placeholder="name@company.com" value={email} error={err} onChange={(e) => setEmail(e.target.value)} />
        <SubmitButton busy={busy} busyText="Sending…" testid="forgot-submit-button">Send reset link</SubmitButton>
        {res && <Alert tone="success" testid="forgot-result">{res.message} The link expires in 1 hour. Check your spam folder if it doesn't arrive within a few minutes.</Alert>}
        <p className="text-center text-sm"><Link to="/login" className={linkCls} data-testid="forgot-back-link">Back to sign in</Link></p>
      </form>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const token = params.get("token");
  const { t } = useI18n();
  const errs = { pw: pwMeetsMin(pw) ? "" : "Use at least 8 characters with letters and numbers", confirm: confirm === pw ? "" : "Passwords don't match" };
  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (busy || errs.pw || errs.confirm) { return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw }); toast.success(t("Password updated. Please sign in.")); nav("/login"); }
    catch (e2) { toast.error(t(errMsg(e2))); }
    setBusy(false);
  };
  if (!token) {
    return (
      <Shell title="Choose a new password">
        <Alert testid="reset-missing-token">This reset link is incomplete. Please request a new one.</Alert>
        <Link to="/forgot-password" className={`mt-4 inline-block text-sm ${linkCls}`} data-testid="reset-request-new-link">Request a new reset link</Link>
      </Shell>
    );
  }
  return (
    <Shell title="Choose a new password" subtitle="At least 8 characters, with letters and numbers.">
      <form onSubmit={submit} className="space-y-4" noValidate data-testid="reset-form">
        <div><PasswordField id="reset-password" label="New password" autoComplete="new-password" value={pw} error={touched ? errs.pw : ""} onChange={(e) => setPw(e.target.value)} /><StrengthMeter value={pw} /></div>
        <PasswordField id="reset-password-confirm" label="Confirm new password" autoComplete="new-password" value={confirm} error={touched ? errs.confirm : ""} onChange={(e) => setConfirm(e.target.value)} />
        <SubmitButton busy={busy} busyText="Updating…" testid="reset-submit-button">Update password</SubmitButton>
      </form>
    </Shell>
  );
}

export function AcceptInvite() {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const token = params.get("token");
  const [inv, setInv] = useState(null);
  const [f, setF] = useState({ name: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/auth/invitation/${token}`).then((r) => setInv(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) { return; }
    if (!f.name.trim() || !pwMeetsMin(f.password)) { setErr("Enter your name and a password with at least 8 characters, letters and numbers."); return; }
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/accept-invite", { token, ...f }); toast.success("Welcome to the team"); setUser(data); nav("/"); } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  return (
    <Shell title="Join your team" subtitle={inv ? `${inv.organisation} invited ${inv.email} as ${inv.role_label}.` : ""}>
      {err && <div className="mb-4"><Alert testid="invite-error">{err}</Alert></div>}
      {inv && (
        <form onSubmit={submit} className="space-y-4" noValidate data-testid="invite-form">
          <Field id="invite-name" label="Full Name" autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <div><PasswordField id="invite-password" label="Password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /><StrengthMeter value={f.password} /></div>
          <SubmitButton busy={busy} busyText="Joining…" testid="invite-submit-button">Accept invitation</SubmitButton>
        </form>
      )}
    </Shell>
  );
}
